import React, { useEffect, useRef, useState } from 'react';

interface GoogleMapProps {
  center: { lat: number; lng: number };
  zoom?: number;
  location?: { latitude: number; longitude: number; address?: string } | null;
  onLocationChange?: (lat: number, lng: number) => void;
  height?: string;
  mapTypeId?: google.maps.MapTypeId;
  showMapTypeControl?: boolean;
  /** Custom marker icon URL (e.g. /images/start_marker.png, /images/end_marker.png) */
  markerIconUrl?: string;
}

const GoogleMap: React.FC<GoogleMapProps> = ({
  center,
  zoom = 13,
  location,
  onLocationChange,
  height = '300px',
  mapTypeId,
  showMapTypeControl = true,
  markerIconUrl,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [marker, setMarker] = useState<google.maps.Marker | null>(null);

  // Initialize map - only run once on mount
  useEffect(() => {
    if (!mapRef.current || !window.google || !window.google.maps) {
      // Wait for Google Maps to load
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps && mapRef.current) {
          clearInterval(checkGoogleMaps);
          initializeMap();
        }
      }, 100);
      return () => clearInterval(checkGoogleMaps);
    } else {
      initializeMap();
    }

    function initializeMap() {
      if (!mapRef.current || !window.google || !window.google.maps) return;

      const defaultMapTypeId = mapTypeId || window.google.maps.MapTypeId.ROADMAP;
      const googleMap = new window.google.maps.Map(mapRef.current, {
        center: new window.google.maps.LatLng(center.lat, center.lng),
        zoom: zoom,
        mapTypeId: defaultMapTypeId,
        fullscreenControl: false,
        zoomControl: true,
        streetViewControl: false,
        mapTypeControl: showMapTypeControl,
      });

      setMap(googleMap);
    }

    return () => {
      if (marker) {
        marker.setMap(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add or remove map click listener when onLocationChange changes (e.g. edit mode toggled)
  useEffect(() => {
    if (!map || !window.google?.maps?.event || !onLocationChange) return;
    const listener = window.google.maps.event.addListener(map, 'click', (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        onLocationChange(e.latLng.lat(), e.latLng.lng());
      }
    });
    return () => {
      window.google?.maps?.event?.removeListener(listener);
    };
  }, [map, onLocationChange]);

  // Update map center and zoom when center or zoom changes
  useEffect(() => {
    if (map && window.google && window.google.maps) {
      map.setCenter(new window.google.maps.LatLng(center.lat, center.lng));
      if (zoom !== undefined) {
        map.setZoom(zoom);
      }
    }
  }, [map, center.lat, center.lng, zoom]);

  // Update map type
  useEffect(() => {
    if (map && mapTypeId) {
      map.setMapTypeId(mapTypeId);
    }
  }, [map, mapTypeId]);

  // Update marker when location changes
  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;

    if (location) {
      const position = new window.google.maps.LatLng(location.latitude, location.longitude);

      if (marker) {
        marker.setPosition(position);
        if (markerIconUrl) {
          marker.setIcon({
            url: markerIconUrl,
            scaledSize: new window.google.maps.Size(48, 48),
            anchor: new window.google.maps.Point(24, 24),
          });
        } else {
          marker.setIcon(undefined);
        }
      } else {
        const newMarker = new window.google.maps.Marker({
          position: position,
          map: map,
          draggable: !!onLocationChange,
          ...(markerIconUrl
            ? {
                icon: {
                  url: markerIconUrl,
                  scaledSize: new window.google.maps.Size(48, 48),
                  anchor: new window.google.maps.Point(24, 24),
                },
              }
            : {}),
        });

        if (onLocationChange) {
          newMarker.addListener('dragend', (e: google.maps.MapMouseEvent) => {
            if (e.latLng) {
              onLocationChange(e.latLng.lat(), e.latLng.lng());
            }
          });
        }

        setMarker(newMarker);
      }

      // Center map on marker
      map.setCenter(position);
      map.setZoom(zoom);
    } else {
      if (marker) {
        marker.setMap(null);
        setMarker(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, location, onLocationChange, zoom, markerIconUrl]);

  return (
    <div
      ref={mapRef}
      style={{
        width: '100%',
        height: height,
        borderRadius: '6px',
        overflow: 'hidden',
      }}
    />
  );
};

export default GoogleMap;

